import { useInfiniteQuery } from '@tanstack/react-query';

import { getPatients, type ListPatientsParams } from '../../../api/patients';
import type { Patient, StageKind } from '../../../types/admin';
import { useAuth } from '../../auth/AuthProvider';

/** Page size per funnel column fetch. The list API caps page_size at 100. */
export const FUNNEL_PAGE_SIZE = 50;

/**
 * Filters shared with the table view, minus the dimensions the board controls
 * itself: each column hardcodes its own `current_stage_kind`, and pagination
 * is driven by the infinite query.
 */
export type FunnelFilters = Omit<ListPatientsParams, 'current_stage_kind' | 'page' | 'page_size'>;

/** Stable React Query key for one funnel column. Shared with the move mutation. */
export function funnelColumnKey(stageKind: StageKind, filters: FunnelFilters) {
  return ['patients', 'funnel', stageKind, filters] as const;
}

export type CycleGroup = {
  readonly cycleNumber: number | null;
  readonly patients: readonly Patient[];
};

function groupByCycle(patients: readonly Patient[]): readonly CycleGroup[] {
  const buckets = new Map<number | null, Patient[]>();
  for (const patient of patients) {
    const key = patient.current_cycle_number ?? null;
    const existing = buckets.get(key);
    if (existing) {
      existing.push(patient);
    } else {
      buckets.set(key, [patient]);
    }
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a - b;
    })
    .map(([cycleNumber, group]) => ({ cycleNumber, patients: group }));
}

/**
 * Loads the patients whose latest cycle is currently at `stageKind`, grouped by
 * cycle number. The column count comes from the API's exact `total`; the
 * per-cycle sub-counts reflect the cards loaded so far (they converge to exact
 * once `hasMore` is false).
 */
export function useFunnelColumn(stageKind: StageKind, filters: FunnelFilters) {
  const { tokens } = useAuth();
  const token = tokens?.access_token;

  const query = useInfiniteQuery({
    queryKey: funnelColumnKey(stageKind, filters),
    enabled: Boolean(token),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getPatients(
        { ...filters, current_stage_kind: stageKind, page: pageParam, page_size: FUNNEL_PAGE_SIZE },
        token!,
      ),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });

  const patients: readonly Patient[] = (query.data?.pages ?? []).flatMap((page) => page.items);
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    total,
    groups: groupByCycle(patients),
    loadedCount: patients.length,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: Boolean(query.hasNextPage),
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => {
      void query.fetchNextPage();
    },
  };
}
